import { TTranslateFunction } from '@modules/localization/types/TranslateFunction';
import getRateLimitMarkdown from '@modules/openCloud/page/limits/utils/getRateLimitMarkdown';
import { apiKeyReferenceDocLink, oauth2ReferenceDocLink } from './linkUtils';

type TProperty = {
  type: string;
  description: string;
  items?: TItems;
  properties?: Record<string, TProperty>;
};

type TItems = {
  properties: Record<string, TProperty>;
};

const getXLongRunningParametersList = (obj: Record<string, any>) => {
  if (!obj || !obj.properties) return '';

  const createList = (items: TItems, depth = 1): string => {
    if (!items || !items.properties) return '';
    const indent = '  '.repeat(depth);
    return Object.keys(items.properties)
      .map((itemKey) => {
        const property = items.properties[itemKey];
        const nestedItemsList = property.items ? `\n${createList(property.items, depth + 1)}` : '';
        const objectKeysList =
          property.type === 'object' && property.properties
            ? `\n${createList({ properties: property.properties }, depth + 1)}`
            : '';
        // indent the lists
        // /\n {2}\|/ is for tables for possible values
        return `\n${indent}- \`${itemKey}\` ${property.type}: ${property.description
          .replace('\n', ' ') // just first new line
          .replace(/\n {2}\|/g, `\n${indent}  |`)}${nestedItemsList}${objectKeysList}`;
      })
      .join('');
  };

  return Object.keys(obj.properties)
    .map((key) => {
      const property = obj.properties[key];
      const propertiesList = property.items ? createList(property.items) : createList(property);
      return `\`${key}\` ${property.type}: ${property.description.replace(
        /\n/g,
        ' ',
      )}${propertiesList}`;
    })
    .join('\n\n');
};

/*
  There's only 5 endpoints with x-long-running-operation-parameters in the cloudV2 spec
  import cloudV2 from '../../reference/cloud/cloud.docs.json';
  
  <SwaggerDocs
  spec={cloudV2}
  server="apis.roblox.com"
  endpoints={[
  { path: "/cloud/v2/universes/{universe_id}/places/{place_id}/instances/{instance_id}:listChildren", method: "get" },
  { path: "/cloud/v2/universes/{universe_id}/places/{place_id}/instances/{instance_id}", method: "get" },
  { path: "/cloud/v2/universes/{universe_id}/places/{place_id}/instances/{instance_id}", method: "patch" },
  { path: "/cloud/v2/universes/{universe_id}/memory-store:flush", method: "post" },
  { path: "/cloud/v2/users/{user_id}:generateThumbnail", method: "get" },
  ]}
  />
  */
export const addLongRunningOperationParameters = (
  translate: TTranslateFunction,
  operation: Record<string, any>,
) => {
  //
  const xLongRunningOperationParameters = operation['x-long-running-operation-parameters'];
  if (xLongRunningOperationParameters) {
    const responseContent = getXLongRunningParametersList(xLongRunningOperationParameters.response);
    const metadataContent = getXLongRunningParametersList(xLongRunningOperationParameters.metadata);

    return `\n\n#### ${translate('Label.Response')}
  
${translate('Description.AsyncResponse', {
  longRunningOperationsLink: '/cloud/reference/patterns#long-running-operations',
})}
  
##### ${translate('Label.Response')}
  
${responseContent || translate('Label.None')}
  
##### ${translate('Label.Metadata')}
  
${metadataContent || translate('Label.None')}`;
  }
  return '';
};

export const addXVisibility = (translate: TTranslateFunction, operation: Record<string, any>) => {
  const xVisibility = operation['x-visibility'] || operation['x-roblox-stability'];
  if (xVisibility === 'BETA') {
    return `\n\n#### ${translate('Label.Beta')}

${translate('Description.Beta')}`;
  }
  return '';
};

export const addScopes = (translate: TTranslateFunction, operation: Record<string, any>) => {
  const scopes: { name: string; description?: string }[] = operation['x-roblox-scopes'];

  if (!scopes || !scopes.length) {
    return '';
  }

  return `\n\n#### ${translate('Label.Scopes')}\n\n${scopes
    .map((scope) => `- \`${scope.name}\`${scope.description ? `: ${scope.description}` : ''}`)
    .join('\n')}`;
};

export const addRateLimits = (translate: TTranslateFunction, operation: Record<string, any>) => {
  const rateLimits = operation['x-roblox-rate-limits'];
  if (!rateLimits) {
    return '';
  }
  return `\n\n#### ${translate('Label.Limits')}\n\n${getRateLimitMarkdown(translate, rateLimits)}`;
};

export const addXRobloxDocs = (translate: TTranslateFunction, operation: Record<string, any>) => {
  let xRobloxDocsDescription = ``;
  const xRobloxDocs = operation['x-roblox-docs'];
  if (!xRobloxDocs) {
    return '';
  }
  const { resource } = xRobloxDocs;
  // Example: { path: "/cloud/v2/universes/{universe_id}/places/{place_id}/luau-execution-session-tasks", method: "post" },
  if (resource.properties) {
    xRobloxDocsDescription += `\n\n#### ${translate('Label.Immutable')}\n\n${translate(
      'Description.ImmutablePlural',
    )}\n\n
  
${Object.keys(resource.properties)
  .map((resourceKey: string) => `- \`${resourceKey}\``)
  .join('\n')}`;
  }
  const xOneOf = resource['x-oneOf'];
  if (xOneOf) {
    xRobloxDocsDescription += `\n\n#### ${translate('Label.OneOf')}
  
${Object.keys(xOneOf)
  .map((key: string) => `- ${key}: ${xOneOf[key].map((item: string) => `\`${item}\``).join(', ')}`)
  .join('\n')}`;
  }

  return xRobloxDocsDescription;
};

export const getSwaggerDocsDescription = (
  translate: TTranslateFunction,
  operation: Record<string, any>,
) => {
  return `${operation.description ? `${operation.description}\n\n` : ''}
${addXVisibility(translate, operation)}
${addScopes(translate, operation)}
${addXRobloxDocs(translate, operation)}
${addRateLimits(translate, operation)}
${addLongRunningOperationParameters(translate, operation)}`.replace(
    /(?<!\/docs)\/cloud/g,
    '/docs/cloud',
  );
};
